import type { AggregatorOrderStatus } from './deliveryAggregatorsApi';

/**
 * Plumbing shared by this screen's three tabs. Kept local rather than reusing
 * `Pages/Integrations/lib.ts`'s copy of the same helpers, for the reason that
 * file's own header gives: the strings and status vocabularies are this
 * module's own, not the integration substrate's.
 */

/** `@/components/ui/badge`'s own `Tone` is module-private; `Pages/Integrations/lib.ts` re-declares the same literal union locally for the same reason — copied here rather than imported. */
export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand';

/**
 * Surface the API's own refusal sentence rather than a generic failure —
 * every `BadRequestException` in this module is plain English, not a coded
 * envelope.
 *
 * Reads **two** shapes, not one: a direct axios catch (every mutation on this
 * screen) carries `error.response.data.message`, but a query read through
 * `useAggregatorOrdersQuery` et al. hands back whatever `baseApi.ts`'s
 * `axiosBaseQuery` already flattened it to — `{ status, message }`, the
 * server's own text already unwrapped onto `.message` with no `.response` at
 * all. `Pages/Integrations/lib.ts`'s own copy of this helper checks only the
 * axios shape, which means the exact sentence a broken list read failed with
 * never reaches its `PanelResult` — only the generic fallback does. Checked
 * here as the reason this copy is not simply that one imported.
 */
export function apiMessage(error: unknown, fallback: string): string {
  const err = error as
    | {
        message?: string;
        response?: { data?: { message?: string | string[] } };
      }
    | null
    | undefined;
  const fromResponse = err?.response?.data?.message;
  if (Array.isArray(fromResponse)) return fromResponse.filter(Boolean).join(' · ');
  if (typeof fromResponse === 'string' && fromResponse.trim()) return fromResponse;
  if (typeof err?.message === 'string' && err.message.trim()) return err.message;
  return fallback;
}

/** The HTTP status of a failed request, when there was a response at all — a 409 here means "already answered", worth a resync rather than just an error toast. */
export function apiStatus(error: unknown): number | null {
  const status = (error as { response?: { status?: unknown } })?.response?.status;
  return typeof status === 'number' ? status : null;
}

export function formatDateTime(value: string | Date | null | undefined, locale: string): string {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

/**
 * The queue's own status vocabulary. `RECEIVED` is `warning` — it is the row
 * asking for a decision, the same visual weight a pending §24.5 approval gets
 * — everything downstream of a staff decision is calmer.
 */
const STATUS_TONE: Record<AggregatorOrderStatus, Tone> = {
  RECEIVED: 'warning',
  ACCEPTED: 'info',
  READY_FOR_PICKUP: 'info',
  COURIER_ASSIGNED: 'info',
  REJECTED: 'danger',
  COMPLETED: 'success',
  CANCELLED: 'neutral',
};

export const toneForAggregatorStatus = (status?: AggregatorOrderStatus | null): Tone =>
  status ? (STATUS_TONE[status] ?? 'neutral') : 'neutral';

/** `ALLOWED` in `delivery-aggregator-orders.service.ts`, mirrored so the UI never offers a transition the server will refuse with a 400. */
export const AGGREGATOR_TRANSITIONS: Record<AggregatorOrderStatus, readonly AggregatorOrderStatus[]> = {
  RECEIVED: ['ACCEPTED', 'REJECTED', 'CANCELLED'],
  ACCEPTED: ['READY_FOR_PICKUP', 'CANCELLED'],
  READY_FOR_PICKUP: ['COURIER_ASSIGNED', 'CANCELLED'],
  COURIER_ASSIGNED: ['COMPLETED', 'CANCELLED'],
  REJECTED: [],
  COMPLETED: [],
  CANCELLED: [],
};
